<template>
  <v-container fluid>
    <v-dialog v-model="customersDialog.show" width="500" scrollable>
      <v-card flat class="pt-2" v-if="customersDialog.model">
        <v-card-title>
          {{ $t('linked_organizations') }}
        </v-card-title>
        <v-card-subtitle class="text-subtitle-1">
          {{ customersDialog.model.email }}
        </v-card-subtitle>
        <v-divider/>
        <v-card-text>
          <v-list>
            <v-list-item v-for="customer in customersDialog.model.customers" :key="customer.id">
              <v-list-item-content>
                <router-link :to="'/customer/' + customer.id" class="d-flex align-center" target="_blank">
                  {{ customer.name }}
                  <v-icon color="primary" small class="ml-1">mdi-open-in-new</v-icon>
                </router-link>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card-text>
        <v-divider/>
        <v-card-actions>
          <v-spacer/>
          <v-btn text color="primary" @click="customersDialog.show = false">
            {{ $t('close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <div class="d-flex justify-space-between align-center">
      <h2>{{ $tc('user', 2) }}</h2>
      <div v-if="userLoaded && user.can('create_user', 'all')">
        <v-btn
          outlined
          color="primary"
          to="/user/create">
          <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
          {{ $t('create_user' )}}
        </v-btn>
        <v-btn
          outlined
          class="ml-2"
          color="primary"
          to="/user/bulk-create">
          <v-icon class="mr-1 ml-n2">mdi-plus-circle-multiple</v-icon>
          {{ $t('bulk_create_users' )}}
        </v-btn>
      </div>
    </div>
    <v-sheet outlined rounded class="px-4 py-2">
      <div class="text-subtitle-1">{{ $t('filter_by') }}</div>
      <v-row dense>
        <v-col lg="4" md="6" cols="12">
          <v-text-field
            :label="$tc('email_address', 1)"
            v-model="searchCriteria.email"
            @keyup.enter="initiateSearch()"
            prepend-inner-icon="mdi-email"/>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('role', 2)"
            v-model="searchCriteria.roles"
            :items="roleOptions"
            prepend-inner-icon="mdi-key"/>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <vf-autocomplete
            small
            :label="$tc('customer', 2)"
            v-model="searchCriteria.customers"
            :items="customers"
            item-text="name"
            item-value="id"
            prepend-inner-icon="mdi-office-building"/>
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.lastLoginAfter"
            :timezone="customerTimezone"
            :label="$t('last_login_after')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <date-picker
            v-model="searchCriteria.createdAfter"
            :timezone="customerTimezone"
            :label="$t('created_after')"
            prepend-inner-icon="mdi-calendar"
            reset-on-timezone-change />
        </v-col>
        <v-col lg="4" md="6" cols="12">
          <v-select
            :label="$t('deleted')"
            v-model="searchCriteria.deletedUsers"
            :items="deletedOptions"
            prepend-inner-icon="mdi-delete"
            item-text="text"
            item-value="value" />
        </v-col>
      </v-row>
      <div class="d-flex justify-end mt-1 mb-2">
        <v-btn outlined color="primary" @click="initiateSearch()" :loading="remoteData.isBusy">
          <v-icon class="mr-1 ml-n1">mdi-magnify</v-icon>
          {{ $t('search') }}
        </v-btn>
        <v-btn outlined color="primary" class="ml-2" @click="resetTable()" :disabled="tableControlsMatchDefaults">
          <v-icon class="mr-1 ml-n1">mdi-filter-remove</v-icon>
          {{ $t('reset') }}
        </v-btn>
      </div>
    </v-sheet>
    <div class="d-flex justify-space-between align-center mt-2">
      <div class="text-subtitle-1 font-weight-bold hidden-md-and-down">{{ $tc('result', 2) }}</div>
      <table-pagination-controls
        :total-rows="remoteData.totalRows"
        :current-page.sync="remoteData.currentPage"
        :rows-per-page.sync="remoteData.rowsPerPage"
        @update:currentPage="refreshResults"
        @update:rowsPerPage="initiateSearch"
        :per-page-options="remoteData.pageOptions"/>
      <div>
        <v-btn
          outlined
          color="primary"
          class="hidden-md-and-down ml-2"
          @click="downloadRemoteDataCsv()"
          :loading="remoteData.isDownloading">
          <v-icon class="mr-1 ml-n1">mdi-tray-arrow-down</v-icon>
          {{ $t('download') }}
        </v-btn>
        <vf-tooltip :text="$t('download')">
          <v-btn class="hidden-lg-and-up" fab text color="primary" @click="downloadRemoteDataCsv()" :loading="remoteData.isDownloading">
            <v-icon>mdi-tray-arrow-down</v-icon>
          </v-btn>
        </vf-tooltip>
      </div>
    </div>
    <v-row id="current-users" class="mt-3">
      <v-col>
        <v-data-table
          hide-default-footer
          class="vf-table mt-2"
          :headers="tableFields"
          :items="remoteData.items"
          :server-items-length="remoteData.totalRows"
          :sort-by.sync="remoteData.sortBy"
          :sort-desc.sync="remoteData.sortDesc"
          :loading="remoteData.isBusy"
          :loading-text="$t('loading')"
          :item-class="rowClassCallback"
          @update:sort-by="initiateSearch()"
          @update:sort-desc="initiateSearch()">
          <template #item.email="row">
            <router-link :to="`/user/update/${row.item.id}`">
              {{ row.value }}
            </router-link>
          </template>
          <template #item.roles="row">
            <v-chip small dark class="mr-1" v-for="(role, index) in row.item.roles" :key="index">
              {{ role.name }}
            </v-chip>
          </template>
          <template #item.customers="row">
            <v-btn fab x-small color="primary" elevation="0" @click="showCustomersDialog(row.item)">
              {{ size(row.value) }}
            </v-btn>
          </template>
          <template #item.last_login="row">
            <if-set :value="row.value">
              <multi-time-display :time="row.value"/>
            </if-set>
          </template>
          <template #item.created="row">
            <multi-time-display :time="row.value"/>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import VfAutocomplete from '@/components/shared/VfAutocomplete'
import VfTooltip from '@/components/shared/VfTooltip'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import TablePaginationControls from '@/components/shared/TablePaginationControls'
import DatePicker from '@/components/shared/DatePicker'
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import DateTimeUtilsMixin from '@/components/mixins/DateTimeUtilsMixin'
import RemoteDataTableMixin from '@/components/mixins/RemoteDataTableMixin'
import BrowserUtilitiesMixin from '@/components/mixins/BrowserUtilitiesMixin'
import UserRecord from '@/orm/models/UserRecord'
import { map, isEmpty, size } from 'lodash'

export default {
  name: 'UserIndex',
  components: {
    VfAutocomplete,
    VfTooltip,
    MultiTimeDisplay,
    TablePaginationControls,
    DatePicker
  },
  mixins: [
    CurrentUserMixin,
    RemoteDataTableMixin,
    DateTimeUtilsMixin,
    BrowserUtilitiesMixin
  ],
  data () {
    return {
      model: 'User',
      roles: [],
      remoteData: {
        sortBy: 'id',
        sortDesc: true
      },
      customersDialog: {
        show: false,
        model: null
      },
      searchCriteria: this.getDefaultSearchCriteria(),
      tableFields: [
        {
          value: 'email',
          text: this.$tc('email'),
          sortable: true
        },
        {
          value: 'roles',
          text: this.$tc('role', 2),
          sortable: false
        },
        {
          value: 'customers',
          text: this.$tc('organizations'),
          sortable: false
        },
        {
          value: 'last_login',
          text: this.$tc('last_login'),
          sortable: true
        },
        {
          value: 'created',
          text: this.$tc('created'),
          sortable: true
        }
      ],
      deletedOptions: [
        {
          value: 'exclude',
          text: this.$t('exclude_deleted')
        },
        {
          value: 'include',
          text: this.$t('include_deleted')
        },
        {
          value: 'show_only',
          text: this.$t('only_show_deleted')
        }
      ]
    }
  },
  computed: {
    roleOptions() {
      return this.roles.map(role => ({
        value: role.code,
        text: role.name
      }))
    }
  },
  methods: {
    size: size,
    /**
     * Sets our data table back to its original state before the user applied filtering and paging changes.
     * @return undefined
     */
    resetTable () {
      this.searchCriteria = this.getDefaultSearchCriteria()
      this.resetRemoteDataTableNavigation()
      this.refreshResults()
    },
    /**
     * Returns the default search criteria for this data table.
     * @return Object
     */
    getDefaultSearchCriteria () {
      return {
        email: null,
        roles: null,
        customers: null,
        lastLoginAfter: null,
        createdAfter: null,
        deletedUsers: 'exclude'
      }
    },
    /**
     * Constructs an object of key value pairs that represent the current search criteria.
     * @return object
     */
    buildSearchCriteria () {
      const expand = [
        'customers',
        'roles'
      ]
      const fields = [
        'id',
        'email',
        'role',
        'last_login',
        'created',
        'deleted',
        'customers.id',
        'customers.name'
      ]
      const searchCriteria = {
        expand: expand.join(','),
        fields: fields.join(','),
        deleted_scope: this.searchCriteria.deletedUsers,
        page: this.remoteData.currentPage,
        'per-page': this.remoteData.rowsPerPage
      }
      if (this.searchCriteria.email != null) {
        searchCriteria.email = this.searchCriteria.email
      }
      if (!isEmpty(this.searchCriteria.roles)) {
        searchCriteria.role = this.searchCriteria.roles.join(',')
      }
      if (!isEmpty(this.searchCriteria.customers)) {
        searchCriteria.customer_id = this.searchCriteria.customers.join(',')
      }
      if (this.searchCriteria.lastLoginAfter != null) {
        searchCriteria.last_login_after = this.searchCriteria.lastLoginAfter
      }
      if (this.searchCriteria.createdAfter != null) {
        searchCriteria.created_after = this.searchCriteria.createdAfter
      }
      if (this.remoteData.sortBy !== null) {
        const sortDirection = this.remoteData.sortDesc ? '-' : ''
        searchCriteria.sort = `${sortDirection}${this.remoteData.sortBy}`
      }
      return searchCriteria
    },
    rowClassCallback (row) {
      return row.deleted != null ? 'deleted' : ''
    },
    showCustomersDialog (user) {
      this.customersDialog.model = user
      this.customersDialog.show = true
    }
  },
  mounted () {
    this.updateBrowserTitle(this.$tc('user', 2))
    this.initiateSearch()
  },
  created () {
    this.$orm('Role').findAll()
      .then(response => {
        this.roles = response.getItems()
      })
      .catch(error => {
        if (error) {
          this.$store.dispatch('globalMessagingModule/showErrorFlashBanner', this.$i18n.t('network_error_message'))
          throw error
        }
      })
  }
}
</script>
